/**
 * HeidiButton - Renders an inline call-to-action button emitted by the LLM as
 * `<heidi-button href="…">Label</heidi-button>` in Evidence chat responses.
 *
 * Security model (defence-in-depth):
 *   1. The backend streaming validator strips disallowed URLs before they
 *      reach the SSE stream (see ml-scribe `apis/v2/chat/heidi_button_validator.py`).
 *   2. This component re-validates every href against an allowlist before
 *      rendering it as a clickable button. Disallowed hrefs render as
 *      non-interactive text — never as a link the user could click.
 *   3. Relative paths are only resolved when the chat is running on an
 *      allowlisted origin (see HEIDI_BUTTON_ALLOWED_HOSTS).
 *
 * Navigation model:
 *   The click handler routes through three branches in order:
 *     1. Settings destinations on the *current* scribe origin → open the
 *        global settings modal in place. The full resolved path (sub-path,
 *        search, hash) is passed through so deep-links survive.
 *     2. Any other same-origin scribe destination (`/library`,
 *        `/scribe/my-templates`, `/evidence/cpd`) → `router.push` for
 *        client-side Next.js navigation. Keeps the user in the same tab on
 *        web and inside the desktop app on Tauri instead of bouncing them
 *        out to the OS browser. "Same origin" is strict equality against
 *        the current window origin (or the env-mapped scribe origin on
 *        Tauri) — an allowlisted *cross-env* URL (e.g. a `scribe.dev`
 *        href while running on prod) is intentionally treated as
 *        external so the host isn't silently rewritten.
 *     3. External destinations (marketing, pricing, off-origin /
 *        cross-env scribe URLs) → new tab via `window.open` on web,
 *        OS default browser via `openUrlInTauri` on desktop.
 *
 * Why a custom tag rather than a markdown link:
 *   A real button affordance + uniform analytics + URL allowlist
 *   re-validation, none of which we'd get from rendering `[Label](URL)`
 *   through the standard markdown pipeline (where it would be treated as
 *   a citation badge — see `mdComponents.a` in `markdown-components.tsx`).
 *
 * When the backend emits this tag:
 *   The backend runs a single parameterised CTA addon
 *   (`apply_product_ctas_addon` in ml-scribe `service/chat/cta_addon_prompt.py`)
 *   with two independent boolean parameters wired to two Flagsmith flags:
 *     • `release-scribe-evidence-product-ctas` →
 *       `emit_button_ctas`. When True, the seven non-billing CTAs are
 *       emitted as `<heidi-button>`. Today's segment: web / desktop /
 *       openapi / unknown. Phase 2 (~2 weeks, pure Flagsmith flip):
 *       all platforms.
 *     • `release-scribe-evidence-product-ctas-mobile-billing-link` →
 *       `emit_billing_as_hyperlink`. When True, billing is a markdown
 *       hyperlink to www.heidihealth.com instead of a `<heidi-button>`.
 *       Today's segment: ios / android. Stays ON for those platforms
 *       indefinitely — the in-app billing menu doesn't exist on
 *       mobile so even a working button would dead-end.
 *   The four (T/F × T/F) modes:
 *     - (T,F) web/desktop today: 8 `<heidi-button>` tags through
 *       this component, including billing.
 *     - (F,T) mobile today: nothing flows through this component —
 *       only the markdown billing link is emitted.
 *     - (T,T) mobile after Phase 2: 7 `<heidi-button>` tags through
 *       this component; billing is the markdown link path.
 *     - (F,F) legacy / off: nothing.
 *   Clients without native rendering for `<heidi-button>` are kept
 *   out of the `emit_button_ctas` segment until they ship support
 *   (they degrade to "no CTA visible" rather than literal markup),
 *   making per-platform rollout a Flagsmith segment edit rather
 *   than a code change. See `manager/chat/cta_prompt_variant.py`
 *   and `service/chat/cta_addon_prompt.py` in ml-scribe for the
 *   full mode matrix.
 */
import { useRouter } from 'next/router';
import { isValidElement, useMemo, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Button } from '@heidi/design-system/button';
import { useSettingsModal } from '@/app-shell/settings/hooks/useSettingsModal';
import { getHostedWebAppOrigin } from '@/helpers/getWebAppOrigin';
import { logger } from '@/utils/logger/logger';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { isTauri, openUrlInTauri } from '@/tauri/utils';
import { track } from '@/lib/analytics/track';

import {
  classifyHeidiButtonHref,
  HEIDI_BUTTON_SETTINGS_ROUTES,
  resolveAndValidateHeidiButtonHref,
} from '../config/heidi-button-allowlist';

interface HeidiButtonProps {
  /** Raw href as emitted by the LLM. May be relative (`/settings/...`) or absolute. */
  href?: string;
  /** Inline label between the tag's opening/closing tokens. Becomes the button text. */
  children?: ReactNode;
  /** Session ID for analytics, when the chat is inside a scribe session. */
  sessionId?: string | null;
  /** Chat ID from chat conversation metadata, for analytics. */
  chatId?: string;
  /** Backend message ID (msg_*) for the assistant response, for analytics. */
  messageId?: string;
}

/**
 * Best-effort extraction of plain text from the React children produced by
 * Streamdown. Used both for the visible label and the analytics `label`
 * property. Falls back to an empty string for non-text children.
 */
const extractLabel = (children: ReactNode): string => {
  if (children === null || children === undefined) return '';
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) {
    return children.map(extractLabel).join('');
  }
  if (isValidElement<{ children?: ReactNode }>(children)) {
    return extractLabel(children.props.children);
  }
  return '';
};

const messages = defineMessages({
  // Used as both the visible button text and the aria-label when the LLM
  // emits an empty inline label between the tag's open/close tokens. Should
  // never normally render — the LLM is instructed to always emit a label —
  // but is kept localised so a degraded path stays consistent with the
  // rest of the UI.
  fallbackLabel: {
    id: 'heidiButton.fallbackLabel',
    defaultMessage: 'Open link',
  },
});

export const HeidiButton = ({
  href,
  children,
  sessionId,
  chatId,
  messageId,
}: HeidiButtonProps): JSX.Element | null => {
  const intl = useIntl();
  const router = useRouter();
  const { open: openSettingsModal } = useSettingsModal();
  const fallbackLabel = intl.formatMessage(messages.fallbackLabel);

  // SSR-safe: window.location.origin is only read inside the memo, which
  // runs on every render. Streamdown is rendered client-side only in the
  // chat surface, so this is fine in practice.
  //
  // Tauri desktop: `window.location.origin` is `tauri://localhost` (not in
  // HEIDI_BUTTON_ALLOWED_HOSTS), so resolve relative paths against the
  // hosted scribe-FE origin instead. Web/desktop-browser flows use the real
  // origin as before.
  const resolved = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const origin = isTauri() ? getHostedWebAppOrigin() : window.location.origin;
    return resolveAndValidateHeidiButtonHref(href, origin);
  }, [href]);

  const label = useMemo(() => extractLabel(children).trim(), [children]);

  if (!resolved) {
    // Disallowed URL or unresolvable relative path. Render the inner text as
    // plain content so the user still sees what the LLM said, but without
    // any clickable affordance pointing at an unknown destination.
    if (href) {
      logger.warn('HeidiButton href blocked by allowlist', {
        href,
        label,
      });
    }
    return <span>{children}</span>;
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();

    // `resolved` is already allowlist-validated and absolute, so `new URL`
    // is expected to succeed. Guarding here keeps a downstream
    // `URL`-parser change or a future relaxation of the validator from
    // silently throwing inside `handleClick` and swallowing the click.
    let resolvedUrl: URL;
    try {
      resolvedUrl = new URL(resolved);
    } catch {
      logger.warn('HeidiButton failed to parse resolved href as a URL', {
        resolved,
      });
      return;
    }

    const destination = classifyHeidiButtonHref(resolved);
    // Deep-link preserved verbatim: any sub-path (`/settings/billing/invoices`),
    // search params, or hash anchor on the resolved href flows through to
    // the in-app branches below. Strip the scheme/host — passing them to
    // `router.push` would force a full reload.
    const internalPath = `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;

    track({
      event_name: TrackingEventName.EvidenceChatCtaClicked,
      props: {
        href: resolved,
        label: label || fallbackLabel,
        destination,
        origin_host: window.location.host,
        chat_id: chatId,
        message_id: messageId,
        session_id: sessionId ?? undefined,
      },
      options: { amplitude: false },
    });

    // "Same scribe origin" — the only origin where Next.js client navigation
    // can actually land the user. On web that's the literal current origin;
    // on Tauri the webview reports `tauri://localhost`, so the effective
    // in-app origin is the hosted scribe host (see `getHostedWebAppOrigin`).
    //
    // Critically: this is strict equality against the *current* origin, not
    // membership in the cross-env allowlist. An allowlisted cross-env URL
    // (e.g. an LLM-emitted `https://scribe.dev.heidihealth.com/library`
    // while the user is on prod) is intentionally NOT considered
    // same-origin — `router.push`-ing its pathname would silently land the
    // user on the prod equivalent instead of the dev environment they were
    // actually being pointed at. Those URLs fall through to the external
    // branch and open in a new tab / OS browser so the host survives.
    const currentScribeOrigin = isTauri()
      ? getHostedWebAppOrigin()
      : window.location.origin;
    const isSameScribeOrigin = resolvedUrl.origin === currentScribeOrigin;

    // Settings destinations (`/settings/billing`, `/settings/account`,
    // `/settings/evidence` — driven by `HEIDI_BUTTON_SETTINGS_ROUTES`)
    // open the global Settings modal in place. The modal is the only
    // settings surface now, so there's no page fallback to route to.
    //
    // We pass `internalPath` (not the static map value) so deep sub-paths
    // and hash anchors on the resolved href flow through end-to-end —
    // matching the generic `router.push` branch below.
    // `useSettingsModal#open` splits on `#` to honour hash anchors;
    // sub-paths it doesn't recognise fall through to its own
    // unknown-route recovery, same as if the URL were typed manually.
    //
    // Note: `splitSettingsModalHref` does NOT split on `?`, so search
    // params would land inside the stored `route` and miss the `SCREENS`
    // lookup. The LLM doesn't emit settings hrefs with search params
    // today, so we forward them verbatim rather than silently stripping
    // them — surfacing the broken case via the modal's existing
    // unknown-route recovery is less misleading than dropping data on
    // the floor without telling anyone.
    const isSettingsDestination = destination in HEIDI_BUTTON_SETTINGS_ROUTES;
    if (isSettingsDestination && isSameScribeOrigin) {
      openSettingsModal(internalPath, 'chat_cta_button');
      return;
    }

    // Any other same-origin scribe destination (`/library`,
    // `/scribe/my-templates`, `/evidence/cpd`) is reachable via Next.js
    // Works on Tauri too: the webview is showing the same Next.js app
    // at `tauri://localhost`, so `router.push` stays inside the
    // desktop app.
    if (isSameScribeOrigin) {
      void router.push(internalPath);
      return;
    }

    // External destinations (marketing, pricing, off-origin / cross-env
    // scribe URLs): open in a new tab on web, or hand off to the OS
    // default browser on Tauri so the URL actually opens — `window.open`
    // inside the Tauri webview either fails silently or opens an in-app
    // popup. Mirrors the existing pattern used by other CTAs
    // (see `usePricingPlansModalCtas`, `useCurrentPlanSection`).
    if (isTauri()) {
      void openUrlInTauri(resolved);
    } else {
      window.open(resolved, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Button
      variant="default"
      size="md"
      onClick={handleClick}
      className="my-2"
      aria-label={label || fallbackLabel}
    >
      {label || fallbackLabel}
    </Button>
  );
};

export default HeidiButton;
